const ALPHABET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'

export function toBase64(bytes: Uint8Array): string {
  const out: string[] = []
  const tail = bytes.length % 3
  const end = bytes.length - tail
  let chunk = ''
  for (let i = 0; i < end; i += 3) {
    const n = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2]
    chunk +=
      ALPHABET[(n >> 18) & 63] +
      ALPHABET[(n >> 12) & 63] +
      ALPHABET[(n >> 6) & 63] +
      ALPHABET[n & 63]
    if (chunk.length >= 8192) {
      out.push(chunk)
      chunk = ''
    }
  }
  if (tail === 1) {
    const n = bytes[end] << 16
    chunk += ALPHABET[(n >> 18) & 63] + ALPHABET[(n >> 12) & 63] + '=='
  } else if (tail === 2) {
    const n = (bytes[end] << 16) | (bytes[end + 1] << 8)
    chunk +=
      ALPHABET[(n >> 18) & 63] +
      ALPHABET[(n >> 12) & 63] +
      ALPHABET[(n >> 6) & 63] +
      '='
  }
  out.push(chunk)
  return out.join('')
}

export class PcmBatcher {
  private buffer: Uint8Array
  private used = 0

  constructor(
    sampleRate: number,
    private readonly onBatch: (pcm: Uint8Array) => void
  ) {
    this.buffer = new Uint8Array(sampleRate * 2)
  }

  push(data: ArrayBuffer) {
    const bytes = new Uint8Array(data)
    let offset = 0
    while (offset < bytes.length) {
      const take = Math.min(
        bytes.length - offset,
        this.buffer.length - this.used
      )
      this.buffer.set(bytes.subarray(offset, offset + take), this.used)
      this.used += take
      offset += take
      if (this.used === this.buffer.length) this.emit()
    }
  }

  flush() {
    this.used -= this.used % 2
    if (this.used >= 2) this.emit()
    this.used = 0
  }

  private emit() {
    const batch = this.buffer.slice(0, this.used)
    this.used = 0
    this.onBatch(batch)
  }
}

export function cameraPacket(jpeg: Uint8Array, pcm: Uint8Array): Uint8Array {
  const packet = new Uint8Array(4 + jpeg.length + pcm.length)
  new DataView(packet.buffer).setUint32(0, jpeg.length, true)
  packet.set(jpeg, 4)
  packet.set(pcm, 4 + jpeg.length)
  return packet
}

export function wavFile(
  chunks: Uint8Array[],
  sampleRate: number
): Uint8Array<ArrayBuffer> {
  const dataLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0)
  const file = new Uint8Array(44 + dataLength)
  const view = new DataView(file.buffer)
  const ascii = (offset: number, text: string) => {
    for (let i = 0; i < text.length; i++)
      view.setUint8(offset + i, text.charCodeAt(i))
  }
  ascii(0, 'RIFF')
  view.setUint32(4, 36 + dataLength, true)
  ascii(8, 'WAVE')
  ascii(12, 'fmt ')
  view.setUint32(16, 16, true)
  view.setUint16(20, 1, true)
  view.setUint16(22, 1, true)
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * 2, true)
  view.setUint16(32, 2, true)
  view.setUint16(34, 16, true)
  ascii(36, 'data')
  view.setUint32(40, dataLength, true)
  let offset = 44
  for (const chunk of chunks) {
    file.set(chunk, offset)
    offset += chunk.length
  }
  return file
}

export function waveformPeaks(chunks: Uint8Array[], buckets: number): number[] {
  const samples = chunks.reduce((sum, chunk) => sum + chunk.length / 2, 0)
  if (!samples) return []
  const perBucket = Math.max(1, Math.floor(samples / buckets))
  const peaks: number[] = []
  let peak = 0
  let count = 0
  for (const chunk of chunks) {
    const view = new DataView(chunk.buffer, chunk.byteOffset, chunk.byteLength)
    for (let i = 0; i + 1 < chunk.length; i += 2) {
      const value = Math.abs(view.getInt16(i, true)) / 32768
      if (value > peak) peak = value
      if (++count === perBucket) {
        peaks.push(peak)
        peak = 0
        count = 0
      }
    }
  }
  if (count) peaks.push(peak)
  const max = Math.max(...peaks, 0.001)
  return peaks.slice(0, buckets).map((value) => value / max)
}
